// =============================================================================
// Release Notes — tenant DTO field probing
// =============================================================================
// The tenant-facing DTOs are not pinned down yet (plan addendum §A.5), so every field the
// tenant surfaces read is probed across the spellings the API could plausibly use. The probing
// lived inline in both `WhatsNew.vue` and `ReleaseDetailPublic.vue`; it lives here once so the
// two pages can never drift apart on what counts as a release's name or publish date.
//
// Every helper degrades to an empty value rather than throwing, so an unknown DTO renders a
// thinner page instead of a broken one.
// =============================================================================

import { ReleaseItemCategory, ReleaseItemCategoryLabels, releaseItemCategoryMeta } from '@ReleaseNotes/config/enums';

const NAME_KEYS = ['name', 'title', 'version'];
// `firstPublishedAt` outranks `lastPublishedAt`: a re-publish corrects the notes, it does not
// move a historical entry to today. The `created*` spellings are the last resort so a release
// the DTO gives no publish date for still carries *a* date rather than none.
// The admin list (`ReleaseNotesList.vue:240`) probes the same spellings for its own columns.
const DATE_KEYS = [
  'publishedAt',
  'firstPublishedAt',
  'lastPublishedAt',
  'publishedOn',
  'publishedDate',
  'publishDate',
  'releaseDate',
  'createdAt',
  'createdOn',
  'createdDate',
  'createDate',
];
const EXCERPT_KEYS = ['summary', 'excerpt', 'description'];
const SLUG_KEYS = ['slug', 'releaseSlug'];
const HTML_KEYS = ['html', 'contentHtml', 'bodyHtml'];
const ITEMS_KEYS = ['items', 'releaseItems', 'notes', 'entries'];
const ITEM_CATEGORY_KEYS = ['category', 'categoryId', 'type', 'itemCategory'];
// A server that pre-aggregates counts saves us walking the items array.
const COUNTS_KEYS = ['categoryCounts', 'counts', 'itemCounts'];

/**
 * First key in `keys` whose value on `source` is present — a non-blank string, or any truthy
 * non-string. Returns an empty string when nothing matches.
 * @param {Object} source DTO
 * @param {string[]} keys candidate property names, most-preferred first
 * @returns {*}
 */
export function firstValue(source, keys) {
  if (!source) return '';
  const key = keys.find((candidate) => {
    const value = source[candidate];
    return typeof value === 'string' ? value.trim().length > 0 : !!value;
  });
  return key ? source[key] : '';
}

/**
 * @param {Object} release
 * @returns {string} display name, never blank
 */
export function releaseName(release) {
  return firstValue(release, NAME_KEYS) || 'Untitled release';
}

/**
 * Publish timestamp, verified to be parseable. A value that no `Date` can read is treated as
 * absent, so callers can format it without guarding — `Invalid Date` never reaches the page.
 * @param {Object} release
 * @returns {string} publish timestamp, or '' when the release carries none
 */
export function releasePublishedAt(release) {
  const value = firstValue(release, DATE_KEYS);
  if (value && !Number.isNaN(new Date(value).getTime())) return value;
  return probeDateLikeKey(release);
}

/**
 * Last-resort date probe. The tenant DTO is not pinned down (plan addendum §A.5) and a date the
 * page cannot name is indistinguishable from no date at all — the changelog then renders undated
 * entries, which is the bug this backstops. Only a key that *reads* like a publish date and holds
 * an ISO-shaped value qualifies, so an id or a formatted label can never be mistaken for one.
 * @param {Object} release
 * @returns {string}
 */
function probeDateLikeKey(release) {
  if (!release || typeof release !== 'object') return '';
  const key = Object.keys(release).find(
    (candidate) =>
      /publish|release|created|date/i.test(candidate) &&
      typeof release[candidate] === 'string' &&
      // An ISO-8601 date or timestamp; anything looser is a label, not a value.
      /^\d{4}-\d{2}-\d{2}/.test(release[candidate]) &&
      !Number.isNaN(new Date(release[candidate]).getTime()),
  );
  return key ? release[key] : '';
}

/**
 * @param {Object} release
 * @returns {string} slug used for the permalink route, or '' when absent
 */
export function releaseSlug(release) {
  if (!release) return '';
  return firstValue(release, SLUG_KEYS) || '';
}

/**
 * Plain-text summary only. An HTML body is never sliced into a preview — a truncated tag would
 * render as broken markup, and the full body is shown inline further down the entry anyway.
 * @param {Object} release
 * @returns {string}
 */
export function releaseExcerpt(release) {
  const value = firstValue(release, EXCERPT_KEYS);
  if (typeof value !== 'string' || /<[a-z][\s\S]*>/i.test(value)) return '';
  return value.trim();
}

/**
 * @param {Object} release detail DTO
 * @returns {string} sanitized body HTML as returned by the server, or ''
 */
export function releaseBodyHtml(release) {
  const value = firstValue(release, HTML_KEYS);
  return typeof value === 'string' ? value : '';
}

/**
 * Stable DOM id / URL hash fragment for a release, so a version is linkable and the scroll-spy
 * has something to target. Slug first; the index only backstops a DTO with no slug at all.
 * @param {Object} release
 * @param {number} index position in the loaded list
 * @returns {string}
 */
export function releaseAnchorId(release, index) {
  const slug = releaseSlug(release);
  const raw = slug || release.id || `release-${index}`;
  return `rel-${String(raw).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

/**
 * Publication year, used to group the version rail. Releases with no date fall into a single
 * "Undated" bucket rather than being dropped from the rail.
 * @param {Object} release
 * @returns {string}
 */
export function releaseYear(release) {
  const value = releasePublishedAt(release);
  if (!value) return 'Undated';
  const year = new Date(value).getFullYear();
  return Number.isNaN(year) ? 'Undated' : String(year);
}

/**
 * Normalises whatever the DTO calls a category onto a `ReleaseItemCategory` value.
 * @param {Object} item release item
 * @returns {number|null} null when the item carries no recognisable category
 */
function itemCategory(item) {
  if (!item || typeof item !== 'object') return null;
  const key = ITEM_CATEGORY_KEYS.find((candidate) => typeof item[candidate] === 'number');
  return key ? item[key] : null;
}

/**
 * Category breakdown for a release, for the icon chips on a changelog entry.
 *
 * Reads a pre-aggregated `categoryCounts` map when the server sends one, otherwise walks the
 * items array. Per CLAUDE.md §7 the walk tallies into a plain object and never spreads items.
 * Returns `[]` — and the chips row disappears entirely — when the DTO carries neither, which is
 * the expected case until the list DTO is pinned down.
 *
 * @param {Object} release list or detail DTO
 * @returns {Array<{ category: number, label: string, icon: string, color: string, count: number }>}
 *   ordered by `ReleaseItemCategory` so the chips never reshuffle between entries
 */
export function releaseCategoryCounts(release) {
  if (!release) return [];
  const tally = {};

  const preAggregated = firstValue(release, COUNTS_KEYS);
  if (preAggregated && typeof preAggregated === 'object' && !Array.isArray(preAggregated)) {
    Object.keys(preAggregated).forEach((key) => {
      const count = preAggregated[key];
      const category = Number(key);
      if (!Number.isNaN(category) && typeof count === 'number' && count > 0) {
        tally[category] = count;
      }
    });
  } else {
    const items = firstValue(release, ITEMS_KEYS);
    if (!Array.isArray(items)) return [];
    for (const item of items) {
      const category = itemCategory(item);
      if (category === null) continue;
      tally[category] = (tally[category] || 0) + 1;
    }
  }

  return Object.values(ReleaseItemCategory)
    .filter((category) => tally[category] > 0)
    .map((category) => ({
      category,
      count: tally[category],
      label: ReleaseItemCategoryLabels[category] || 'Other',
      ...releaseItemCategoryMeta(category),
    }));
}

/**
 * Accepts either a bare array or an `{ items, total }` envelope (plan addendum §A.5).
 * @param {*} data resolved response payload
 * @returns {{ items: Array, total: number }}
 */
export function unwrapReleaseList(data) {
  if (Array.isArray(data)) return { items: data, total: data.length };
  if (data && Array.isArray(data.items)) {
    return {
      items: data.items,
      total: typeof data.total === 'number' ? data.total : data.items.length,
    };
  }
  return { items: [], total: 0 };
}
